import { showToast } from "@caspeco/casper-ui-library";
import { osName } from "react-device-detect";
import {
	haveActivePushToken,
	registerPushtokenAsync,
	unregisterPushNotificationAsync,
} from "../services/api/user-api";
import { useMutation, useQuery } from "@tanstack/react-query";
import queryClient from "../query-client";
import { getValueFromApp } from "../utils/app-helper";
import { useTranslation } from "react-i18next";
import { QueryKeys } from "../types/query-key-enum";
import { useState } from "react";
import { useAppContext } from "./use-app-context";

export const usePushNotifications = (enabled: boolean = true) => {
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const { safeArea } = useAppContext();

	const { data: pushToken } = useQuery({
		queryKey: [QueryKeys.PushNotificationActiveInOS],
		queryFn: () => getValueFromApp("getCurrentNotificationPermission"),
		enabled: enabled,
	});

	const generateNewToken = useMutation({
		mutationFn: () => getValueFromApp("RegisterPush"),
		onSuccess: (token) => {
			if (token) {
				queryClient.invalidateQueries({ queryKey: [QueryKeys.PushNotificationActiveInOS] });
				internalActivatePush.mutateAsync(token);
			} else {
				setIsLoading(false);
			}
		},
		onError: () => {
			setIsLoading(false);
		},
	});

	const { data: isActivated } = useQuery({
		queryKey: [QueryKeys.PushNotificationIsActivated],
		queryFn: () => haveActivePushToken(pushToken!),
		enabled: enabled && Boolean(pushToken),
	});

	const internalActivatePush = useMutation({
		mutationFn: (token: string) => registerPushtokenAsync(token, osName),
		onSuccess: (data) => {
			if (data) {
				queryClient.invalidateQueries({ queryKey: [QueryKeys.PushNotificationIsActivated] });
			}
			setIsLoading(false);
		},
		onError: () => {
			showToast({
				type: "error",
				description: t("pushnotification_activate_fail"),
				distanceToBottom: safeArea.bottomAsPx(),
			});
			setIsLoading(false);
		},
	});

	const internalRemovePush = useMutation({
		mutationFn: () => unregisterPushNotificationAsync(),
		onSuccess: (data) => {
			if (data) {
				queryClient.invalidateQueries({ queryKey: [QueryKeys.PushNotificationIsActivated] });
			}
		},
		onError: () => {
			showToast({
				type: "error",
				description: t("pushnotification_deactivate_fail"),
				distanceToBottom: safeArea.bottomAsPx(),
			});
		},
	});

	const activatePush = () => {
		setIsLoading(true);
		if (pushToken) {
			internalActivatePush.mutateAsync(pushToken);
		} else {
			generateNewToken.mutate();
		}
	};

	const removePush = () => {
		internalRemovePush.mutate();
	};

	return { isActivated: isActivated ?? false, activatePush, removePush, isLoading };
};
